import type {
  WorkbenchPreviewRecoveryReason,
  WorkbenchPreviewRecoveryStep,
} from '../stores/workbench';

/**
 * Ordered recovery for a preview that stopped working because of the in-browser environment
 * (Vite crashed, never became ready, or the iframe stayed silent). Each step is tried in order
 * and the ladder stops at the first one that brings the dev server back:
 *
 * 1. `restart-dev-server`: kill and respawn Vite, up to 3 times, waiting 1s, 2s, 4s first.
 * 2. `remount-dependencies`: remount `node_modules` from the OPFS cache, rewrite the workspace
 *    files, restart Vite.
 * 3. `reboot-webcontainer`: tear the WebContainer down and boot a new one with the same files.
 *
 * When every step fails the caller shows "Reload" — safe because the turn and snapshot are
 * already saved. None of these steps call the AI, so recovery never spends credits.
 *
 * Plan: PROTOTYPER_STABILITY_TECH_PLAN.md §6 (P2.2).
 */

export const PREVIEW_RECOVERY_STEPS: readonly WorkbenchPreviewRecoveryStep[] = [
  'restart-dev-server',
  'remount-dependencies',
  'reboot-webcontainer',
];

export const DEV_SERVER_RESTART_BACKOFF_MS: readonly number[] = [1_000, 2_000, 4_000];

/**
 * A recovery that "worked" but is needed again within this window did not really fix the
 * problem, so the next run starts one step further down instead of repeating the same step.
 */
export const PREVIEW_RECOVERY_ESCALATION_WINDOW_MS = 120_000;

/**
 * The whole recovery run. Past it the preview offers "Reload" instead of spinning on a step
 * that never returns.
 */
export const PREVIEW_RECOVERY_DEADLINE_MS = 300_000;

/**
 * A WebContainer that does not answer a file read cannot restart Vite or remount anything, so
 * recovery goes straight to a reboot instead of spending minutes on steps that cannot work.
 */
export function recoveryStartForWorkspace(
  startAt: WorkbenchPreviewRecoveryStep,
  responsive: boolean
): WorkbenchPreviewRecoveryStep {
  return responsive ? startAt : 'reboot-webcontainer';
}

export interface PreviewRecoveryActions {
  /** Resolves true once Vite reports `server-ready` again. */
  restartDevServer: () => Promise<boolean>;
  remountDependencies: () => Promise<boolean>;
  rebootWebContainer: () => Promise<boolean>;
}

export interface PreviewRecoveryRun {
  at: number;
  reason: WorkbenchPreviewRecoveryReason;
  recovered: boolean;
  step?: WorkbenchPreviewRecoveryStep;
}

export interface RunPreviewRecoveryOptions {
  startAt?: WorkbenchPreviewRecoveryStep;
  onStep?: (step: WorkbenchPreviewRecoveryStep, attempt: number) => void;
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

async function attempt(action: () => Promise<boolean>): Promise<boolean> {
  try {
    return await action();
  } catch {
    return false;
  }
}

export async function runPreviewRecovery(
  actions: PreviewRecoveryActions,
  options: RunPreviewRecoveryOptions = {}
): Promise<{ recovered: boolean; step?: WorkbenchPreviewRecoveryStep }> {
  const sleep = options.sleep ?? defaultSleep;
  const startIndex = Math.max(
    0,
    PREVIEW_RECOVERY_STEPS.indexOf(options.startAt ?? 'restart-dev-server')
  );

  for (const step of PREVIEW_RECOVERY_STEPS.slice(startIndex)) {
    if (step === 'restart-dev-server') {
      for (let i = 0; i < DEV_SERVER_RESTART_BACKOFF_MS.length; i++) {
        options.onStep?.(step, i + 1);
        await sleep(DEV_SERVER_RESTART_BACKOFF_MS[i]);
        if (await attempt(actions.restartDevServer)) return { recovered: true, step };
      }
      continue;
    }

    options.onStep?.(step, 1);
    const action =
      step === 'remount-dependencies' ? actions.remountDependencies : actions.rebootWebContainer;
    if (await attempt(action)) return { recovered: true, step };
  }

  return { recovered: false };
}

/**
 * Where the next recovery run should start, given earlier runs in this tab. Returns `null`
 * when there is nothing left to try (the last step already failed to hold), so the caller
 * goes straight to the "Reload" state instead of looping.
 */
export function nextPreviewRecoveryStart(
  history: readonly PreviewRecoveryRun[],
  now: number
): WorkbenchPreviewRecoveryStep | null {
  const last = history[history.length - 1];
  if (!last || now - last.at > PREVIEW_RECOVERY_ESCALATION_WINDOW_MS) return 'restart-dev-server';
  if (!last.recovered || !last.step) return null;
  const next = PREVIEW_RECOVERY_STEPS[PREVIEW_RECOVERY_STEPS.indexOf(last.step) + 1];
  return next ?? null;
}
